import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SearchableSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@jem-open/jem-ui';

import Swal from 'sweetalert2';
import moment from 'moment';
import axios from 'axios';

import apiResponseHandler from '../../../utils/apiResponseHandler';
import { environment } from '../../../environment/environment';
import { localStorageService } from '../../../services';

/**
 * @summary AddNewSubscriptionBatchModal component
 *
 * @param {Props} props
 * @component
 */
const AddNewSubscriptionBatchModal = (props) => {
  const [state, setState] = useState({
    employers: [],
    subscriptionOptions: [],
    activeEmployer: null,
    newSubscriptionBatchPayload: {},
    lastSubscriptionBatch: null,
    selectedSubscriptionId: null,
  });

  const {
    toggleAddNewBatchModal,
    handleShowAddNewBatchModal,
    getSubscriptionBatches,
  } = props;

  const getEmployers = () => {
    const headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    axios.get(`${environment.baseUrl}/employers/`, { headers }).then((res) => {
      setState((prev) => ({
        ...prev,
        employers: res.data,
        loading: false,
      }));
    });
  };

  useEffect(() => {
    getEmployers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const employerOptions = useMemo(
    () =>
      (state.employers || []).map((employer) => ({
        value: String(employer.id),
        label: employer.company_name,
      })),
    [state.employers]
  );

  const handleSubscriptionChange = (selectedSubscriptionId) => {
    axios
      .get(
        `${environment.baseUrl}/subscription_batch/subscription/${selectedSubscriptionId}/`
      )
      .then((res) => {
        const lastSubscriptionBatch = res.data;
        // Prefill the start fields from the previous batch using formats the
        // native inputs accept (date: YYYY-MM-DD, time: HH:mm).
        const prefill =
          lastSubscriptionBatch?.end_datetime
            ? {
                startDate: moment(lastSubscriptionBatch.end_datetime).format(
                  'YYYY-MM-DD'
                ),
                startDateTime: moment(
                  lastSubscriptionBatch.end_datetime
                ).format('HH:mm'),
              }
            : {};
        setState((prev) => ({
          ...prev,
          lastSubscriptionBatch,
          loading: false,
          selectedSubscriptionId,
          newSubscriptionBatchPayload: {
            ...prev.newSubscriptionBatchPayload,
            // Clear any stale start fields before overlaying the new
            // selection's prefill (empty when it has no previous batch).
            startDate: '',
            startDateTime: '',
            ...prefill,
          },
        }));
      });
  };

  const handleEmployerChange = (value) => {
    const matchingEmployer = state.employers.find(
      (employer) => employer.id === parseInt(value, 10)
    );
    setState((prev) => ({
      ...prev,
      activeEmployer: matchingEmployer || null,
      lastSubscriptionBatch: null,
      selectedSubscriptionId: null,
      // Changing employer invalidates the subscription selection, so drop any
      // start fields prefilled from the previous subscription.
      newSubscriptionBatchPayload: {
        ...prev.newSubscriptionBatchPayload,
        startDate: '',
        startDateTime: '',
      },
    }));
  };

  const handleInputChange = (event) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;

    setState((prev) => ({
      ...prev,
      newSubscriptionBatchPayload: {
        ...prev.newSubscriptionBatchPayload,
        [name]: value,
      },
    }));
  };

  const showValidation = (title) => {
    Swal.fire({
      title,
      icon: 'warning',
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  const handleSubmit = () => {
    const user = localStorageService.getUserOrThrow();
    const currentUserId = user.id;

    const {
      lastSubscriptionBatch,
      newSubscriptionBatchPayload,
      activeEmployer,
      selectedSubscriptionId,
    } = state;

    // Guard against an incomplete form so we never post an "Invalid date".
    if (!activeEmployer) {
      showValidation('Please select an employer.');
      return;
    }
    if (!selectedSubscriptionId) {
      showValidation('Please select a subscription.');
      return;
    }

    const startMoment = lastSubscriptionBatch
      ? moment(`${lastSubscriptionBatch.end_datetime}`).utc()
      : moment(
          `${newSubscriptionBatchPayload.startDate} ${newSubscriptionBatchPayload.startDateTime}`
        );
    const endMoment = moment(
      `${newSubscriptionBatchPayload.endDate} ${newSubscriptionBatchPayload.endDateTime}`
    );

    if (!startMoment.isValid()) {
      showValidation('Please provide a valid start date and time.');
      return;
    }
    if (!endMoment.isValid()) {
      showValidation('Please provide a valid end date and time.');
      return;
    }

    const payload = {
      /** set start date of new batch to the end date of the last batch */
      start_date: startMoment.format(),
      end_date: endMoment.format(),
      employer: activeEmployer.id,
      subscription: selectedSubscriptionId,
      requested_by: currentUserId,
    };
    const url = `${environment.baseUrl}/subscription_batch_job/`;

    axios
      .post(url, payload)
      .then(() => {
        Swal.fire({
          title: 'Subscription Batch Job successfully submitted.',
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 10000,
          timerProgressBar: true,
        });
        handleShowAddNewBatchModal();
        getSubscriptionBatches();
      })
      .catch((error) => {
        apiResponseHandler.handleApiError(
          'Error submitting Subscription Batch Job',
          error
        );
        setState((prev) => ({ ...prev, containerState: 'STATE_ERROR' }));
      });
  };

  const startDateReadOnly =
    !!state.lastSubscriptionBatch && state.lastSubscriptionBatch.last_batch_id;

  return (
    <Dialog
      open={toggleAddNewBatchModal}
      onOpenChange={(next) => {
        if (!next) handleShowAddNewBatchModal();
      }}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate New Subscription Batch</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto">
          <div className="flex flex-col gap-xxs">
            <Label htmlFor="employerSelect">Employer</Label>
            <SearchableSelect
              options={employerOptions}
              value={
                state.activeEmployer ? String(state.activeEmployer.id) : ''
              }
              placeholder="Search employer..."
              searchPlaceholder="Search employer..."
              emptyMessage="No employers found."
              onValueChange={handleEmployerChange}
            />
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="selectedSubscription">Subscription</Label>
            <Select
              value={
                state.selectedSubscriptionId
                  ? String(state.selectedSubscriptionId)
                  : ''
              }
              onValueChange={handleSubscriptionChange}
              disabled={!state.activeEmployer}>
              <SelectTrigger id="selectedSubscription">
                <SelectValue placeholder="Select subscription" />
              </SelectTrigger>
              <SelectContent>
                {!!state.activeEmployer &&
                  state.activeEmployer.subscriptions &&
                  state.activeEmployer.subscriptions.map((subscription) => (
                    <SelectItem
                      key={subscription.id}
                      value={String(subscription.id)}>
                      {subscription.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              type="date"
              name="startDate"
              id="startDate"
              placeholder="Batch Start Date"
              onChange={handleInputChange}
              required
              readOnly={startDateReadOnly}
              value={state.newSubscriptionBatchPayload.startDate || ''}
            />
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="startDateTime">Start Date Time</Label>
            <Input
              type="time"
              name="startDateTime"
              id="startDateTime"
              onChange={handleInputChange}
              readOnly={startDateReadOnly}
              value={state.newSubscriptionBatchPayload.startDateTime || ''}
            />
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              type="date"
              name="endDate"
              id="endDate"
              onChange={handleInputChange}
              value={state.newSubscriptionBatchPayload.endDate || ''}
            />
          </div>

          <div className="flex flex-col gap-xxs">
            <Label htmlFor="endDateTime">End Date Time</Label>
            <Input
              type="time"
              name="endDateTime"
              id="endDateTime"
              onChange={handleInputChange}
              value={state.newSubscriptionBatchPayload.endDateTime || ''}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleShowAddNewBatchModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

AddNewSubscriptionBatchModal.defaultProps = {};

AddNewSubscriptionBatchModal.propTypes = {
  toggleAddNewBatchModal: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
    .isRequired,
  handleShowAddNewBatchModal: PropTypes.func.isRequired,
  getSubscriptionBatches: PropTypes.func.isRequired,
};

export default AddNewSubscriptionBatchModal;
